/**
 * Authentication module for handling user login state
 * This module interacts with the WordPress REST API AuthBridge endpoint
 * 
 * @package PocketOption
 */

/**
 * Check user authentication status
 * 
 * @returns {Promise<Object>} Authentication status object
 */
const checkAuthStatus = async () => {
  try {
    // Send request to the AuthBridge API endpoint
    const response = await fetch(`${pocket.siteurl}/wp-json/authbridge/v1/user`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    });

    // Check if the response is ok
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // Parse the JSON response
    const userData = await response.json();
    
    // Check if user is logged in
    if (userData?.logged_in === true && userData?.uid) {
      // User is authenticated
      return {
        isAuthenticated: true,
        userData: {
          uid: userData.uid,
          nickname: userData.nickname,
          avatar: userData.avatar,
          userLevel: userData.user_level
        }
      };
    } else {
      // User is not authenticated
      return {
        isAuthenticated: false,
        userData: null
      };
    }
  } catch (error) {
    console.error('Authentication check failed:', error);
    return {
      isAuthenticated: false,
      userData: null,
      error: error.message
    };
  }
};

/**
 * Handle user authentication flow
 * 
 * @param {Function} onAuthenticated - Callback for successful authentication
 * @param {Function} onUnauthenticated - Callback for unauthenticated users
 * @param {Function} onError - Callback for error handling
 */
const handleAuthentication = async (onAuthenticated, onUnauthenticated, onError) => {
  try {
    const authStatus = await checkAuthStatus();
    
    if (authStatus.isAuthenticated) {
      if (typeof onAuthenticated === 'function') {
        onAuthenticated(authStatus.userData);
      }
    } else {
      if (typeof onUnauthenticated === 'function') {
        onUnauthenticated();
      }
    }

    return authStatus;
  } catch (error) {
    if (typeof onError === 'function') {
      onError(error);
    } else {
      console.error('Authentication error:', error);
    }
  }
};

export { checkAuthStatus, handleAuthentication };